import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { safeLocalStorage } from "@/lib/safeStorage";
import { isConnectionError } from "@/components/ConnectionRetry";

/**
 * Componente que escuta mudanças no estado de autenticação
 * e invalida o cache do React Query quando o usuário muda
 * 
 * OTIMIZAÇÃO: Adiciona retry com backoff exponencial para falhas de refresh token
 */
export function AuthStateListener() {
  const queryClient = useQueryClient();
  const refreshRetryCount = useRef(0);
  const maxRefreshRetries = 5;

  // Retry handler para refresh token com backoff exponencial
  const handleRefreshError = useCallback(async (error: unknown) => {
    if (isConnectionError(error) && refreshRetryCount.current < maxRefreshRetries) {
      const delay = Math.min(1000 * Math.pow(2, refreshRetryCount.current), 30000);
      console.log(`[Auth] Refresh falhou (erro de conexão), tentando novamente em ${delay}ms...`);
      
      refreshRetryCount.current += 1;
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      try {
        const { error: retryError } = await supabase.auth.refreshSession();
        if (!retryError) {
          refreshRetryCount.current = 0;
          console.log("[Auth] Refresh bem-sucedido após retry");
        } else if (isConnectionError(retryError)) {
          handleRefreshError(retryError);
        }
      } catch (e) {
        if (isConnectionError(e)) {
          handleRefreshError(e);
        }
      }
    } else if (refreshRetryCount.current >= maxRefreshRetries) {
      console.error("[Auth] Máximo de retries de refresh atingido");
      refreshRetryCount.current = 0;
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log(`[Auth] Evento detectado: ${event}`);
      
      // INITIAL_SESSION sem sessão = limpar qualquer resíduo
      if (event === 'INITIAL_SESSION' && !session) {
        console.log(`[Auth] Sem sessão inicial, limpando resíduos`);
        safeLocalStorage.removeItem('current-org-id');
        safeLocalStorage.removeItem('current-pipeline-id');
        safeLocalStorage.removeItem('current-partner-id');
        queryClient.clear();
        return;
      }
      
      // Quando o usuário faz login, logout, ou atualiza
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        // Limpar localStorage de organização/partner anterior ao fazer logout
        if (event === 'SIGNED_OUT') {
          safeLocalStorage.removeItem('current-org-id');
          safeLocalStorage.removeItem('current-pipeline-id');
          safeLocalStorage.removeItem('current-partner-id');
          queryClient.clear();
          console.log(`[Auth] Cache e localStorage limpos após SIGNED_OUT`);
          return;
        }
        
        // Para login/update, invalidar SINCRONAMENTE (sem setTimeout)
        // O React Query vai fazer refetch quando os componentes precisarem
        console.log(`[Auth] Invalidando queries após ${event}...`);
        
        // Grupo 0: dados de admin/partner (prioridade máxima)
        queryClient.invalidateQueries({ queryKey: ["user-admin-role"] });
        queryClient.invalidateQueries({ queryKey: ["is-partner-admin"] });
        queryClient.invalidateQueries({ queryKey: ["current-partner-id"] });
        queryClient.invalidateQueries({ queryKey: ["current-partner-data"] });
        queryClient.invalidateQueries({ queryKey: ["partner-member-info"] });
        queryClient.invalidateQueries({ queryKey: ["partner-orgs"] });
        queryClient.invalidateQueries({ queryKey: ["partner-finance-clients"] });
        queryClient.invalidateQueries({ queryKey: ["partner-finance-payments"] });
        
        // Grupo 1: dados essenciais de org
        queryClient.invalidateQueries({ queryKey: ["organizations"] });
        queryClient.invalidateQueries({ queryKey: ["current-org-id"] });
        queryClient.invalidateQueries({ queryKey: ["user-role"] });
        
        // Grupo 2: dados de negócio
        queryClient.invalidateQueries({ queryKey: ["leads"] });
        queryClient.invalidateQueries({ queryKey: ["pipelines"] });
        queryClient.invalidateQueries({ queryKey: ["funnel-stages"] });
        
        // Grupo 3: dados secundários
        queryClient.invalidateQueries({ queryKey: ["lembretes"] });
        queryClient.invalidateQueries({ queryKey: ["vendedores"] });
        queryClient.invalidateQueries({ queryKey: ["org-members"] });
        
        // Grupo 4: dados de tarefas (garantir visibilidade entre membros)
        queryClient.invalidateQueries({ queryKey: ["tasks"] });
        queryClient.invalidateQueries({ queryKey: ["task-subtasks"] });
        queryClient.invalidateQueries({ queryKey: ["task-activity"] });
        
        console.log(`[Auth] Todas as queries invalidadas após ${event}`);
      }

      // OTIMIZAÇÃO: Detectar falhas de refresh token e fazer retry
      if (event === 'TOKEN_REFRESHED') {
        refreshRetryCount.current = 0;
      }
    });

    // Interceptar erros de refresh
    const originalRefresh = supabase.auth.refreshSession.bind(supabase.auth);
    supabase.auth.refreshSession = async () => {
      try {
        const result = await originalRefresh();
        if (result.error && isConnectionError(result.error)) {
          handleRefreshError(result.error);
        }
        return result;
      } catch (error) {
        if (isConnectionError(error)) {
          handleRefreshError(error);
        }
        throw error;
      }
    };

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient, handleRefreshError]);

  // Este componente não renderiza nada
  return null;
}
